import * as React from "react";
import { cn } from "@/lib/utils";
import { BackgroundRatio } from "@/services/bedrock/dtos/BackgroundAsset";
import { AspectRatio } from "@/services/bedrock/dtos/DesignPackage";
import { useHoloTiming } from "./holoTiming";
import { SocoAiLogo } from "./SocoAiLogo";

export interface HoloShimmerProps {
  aspectRatio: AspectRatio | BackgroundRatio;
  label?: string;
  sublabel?: string;
  showLogo?: boolean;
  className?: string;
}

const ASPECT_CLASS: Record<AspectRatio | BackgroundRatio, string> = {
  square_1_1: "aspect-square",
  portrait_4_5: "aspect-[4/5]",
  portrait_9_16: "aspect-[9/16]",
  landscape_4_3: "aspect-[4/3]",
};

const BLOBS = [
  { color: "bg-brand-blue/35", position: "-top-1/4 -left-1/4", motion: "animate-holo-drift-a" },
  { color: "bg-brand-periwinkle/30", position: "top-1/4 -right-1/4", motion: "animate-holo-drift-b" },
  { color: "bg-brand-magenta/25", position: "-bottom-1/4 left-1/6", motion: "animate-holo-drift-c" },
];

/**
 * Loading placeholder for a slide or post image: a slow aurora of blurred brand tints drifting out
 * of step behind the SocialCoach AI logo.
 */
export const HoloShimmer: React.FC<HoloShimmerProps> = ({
  aspectRatio,
  label,
  sublabel,
  showLogo = true,
  className,
}) => {
  const timing = useHoloTiming();
  return (
    <div
      data-slot="holo-shimmer"
      style={timing}
      role="status"
      aria-live="polite"
      className={cn(
        "relative isolate w-full overflow-hidden rounded-2xl bg-holo-linear",
        ASPECT_CLASS[aspectRatio],
        className
      )}
    >
      {BLOBS.map(blob => (
        <span
          key={blob.motion}
          aria-hidden="true"
          data-slot="holo-aurora"
          className={cn(
            "pointer-events-none absolute -z-10 size-3/4 rounded-full blur-2xl motion-reduce:animate-none",
            blob.color,
            blob.position,
            blob.motion
          )}
        />
      ))}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
        {showLogo && <SocoAiLogo variant="color" height={22} />}
        {label && <div className="text-sm font-semibold text-slate-700">{label}</div>}
        {sublabel && <div className="text-xs text-slate-500">{sublabel}</div>}
      </div>
    </div>
  );
};
